import type { PreOrderSourceType } from "@/app/api/types/preOrder";

export type StatusFilter =
  | "all"
  | "pending"
  | "confirmed"
  | "exported"
  | "cancelled"
  | "ai_proposals";

export type DocumentTypeValue = "orders" | "avoirs" | "purchases";
export type DocumentTypeFilter = DocumentTypeValue[];

export type Channel = "whatsapp" | "sms" | "email" | "internal";
export type ChannelFilter = PreOrderSourceType[];

export type FilterMode = "orders" | "flows";

export interface FilterState {
  sources: number[];
  /**
   * Specific communication-account IDs to keep. Compound keys encoding the
   * source type and account UUID, e.g. `"0:<emailAccountId>"` (email),
   * `"1:<instanceId>"` (WhatsApp), `"5:<smsAccountId>"` (SMS). When set,
   * only pre-orders whose resolved account matches are shown — acts as a
   * refinement within the broader `sources` source-type filter.
   */
  sourceAccountIds?: string[];
  /**
   * Employee IDs to keep. The sentinel `NO_EMPLOYEE_FILTER_ID` represents
   * "pre-orders without an assigned employee" and may sit alongside real IDs.
   */
  employees: string[];
  /**
   * Location IDs to keep. The sentinel `NO_LOCATION_FILTER_ID` represents
   * "pre-orders without an assigned location" and may sit alongside real IDs.
   */
  locations: string[];
  partnerIds?: string[];
  productIds?: string[];
  myPartnersOnly?: boolean;
  clientGroupIds?: string[];
  /**
   * User-group ("agence") IDs to keep — gated by the `showGroupRecap` flag.
   * The sentinel `NO_GROUP_FILTER_ID` represents "pre-orders without a group"
   * and may sit alongside real IDs. Matches on `pre_orders.group_id`.
   */
  groupIds?: string[];
  /**
   * Owner user ("personne") IDs to keep — matches on `pre_orders.user_id`
   * (the account the order came through), NOT `employee_id`. Set by the
   * monitoring "Aller voir" deep-link so recap reproduces the exact positions
   * a monitoring person-row counted. Distinct from `employees` (assigned
   * employee) so both can coexist.
   */
  userIds?: string[];
}

/** Sentinel in `FilterState.employees` matching pre-orders whose `employee_id` is null/undefined. */
export const NO_EMPLOYEE_FILTER_ID = "__no_employee__";

/** Sentinel in `FilterState.locations` matching pre-orders whose `location_id` is null/undefined. */
export const NO_LOCATION_FILTER_ID = "__no_location__";

/** Sentinel in `FilterState.groupIds` matching pre-orders whose `group_id` is null/undefined. */
export const NO_GROUP_FILTER_ID = "__no_group__";

export interface DocumentTypeCounts {
  orders: number;
  avoirs: number;
  purchases: number;
}

export interface FilterCounts {
  all: number;
  pending: number;
  confirmed: number;
  exported: number;
  cancelled: number;
  export_failed: number;
  /** "Créée par IA" — orders the AI owns outright (status AI_PROPOSAL). */
  ai_created: number;
  /** "Modifiée par IA" — pending AI suggestion blocks on non-AI-created orders. */
  ai_modified: number;
  /**
   * Number of tombstoned (soft-deleted) pre-orders in the current scope.
   * Used by the "Deleted orders" header banner when `showDeletedOnly` is on.
   * Excluded from the regular `all` count.
   */
  deleted: number;
}
